"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PrimaryButton from "@/components/shared/PrimaryButton";
import { EXTENDED_INNER_PAGES, STANDARD_INNER_PAGES, displayPageCount, type BookPageCount } from "@/lib/comix/book-length";

// Same static product photo per tier for every book — not generated per run.
// The picker is just illustrating "more pages, thicker book"; it isn't meant to
// represent this customer's own cover art. Both photos have a white bottom
// third, which is where the label sits.
const BOOK_PHOTO: Record<number, string> = {
  [STANDARD_INNER_PAGES]: "/images/comix/size-picker-14-pages.png",
  [EXTENDED_INNER_PAGES]: "/images/comix/size-picker-32-pages.png",
};

// 3px primary-gradient ring for the selected card, drawn as a masked overlay so
// it never shifts the layout (the unselected card has a 1px 10% black stroke).
const SELECTED_RING: React.CSSProperties = {
  padding: 3,
  background: "linear-gradient(to right, #F52F81 0%, #FD7468 100%)",
  WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
  WebkitMaskComposite: "xor",
  mask: "linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)",
};

export interface SizePickerItem {
  /** A bag line id (BagDrawer) or just the runId (direct-checkout callers like
   *  UnlockClient / the /comix/checkout page, which have no cart line yet). */
  id: string;
  title?: string;
  initialPageCount?: BookPageCount;
  /** Flat upcharge for the Extended tile, e.g. 10 → "(+$10)". Standard shows
   *  no price — the base price is already shown on the page behind this sheet. */
  extraPagesUpcharge: number;
  /** Price of the Standard book in dollars; when set each card also shows its price per book. */
  basePrice?: number;
}

export interface SizeSelection {
  id: string;
  pageCount: BookPageCount;
}

/**
 * Shown before a comic is added to the bag or sent to Shopify checkout — lets
 * the shopper pick Standard (12 pages) or Extended (28 pages, +upcharge).
 * Picking Extended triggers real post-purchase generation of more pages (see
 * order-paid webhook + generate-more) — this sheet only handles the
 * price/variant side. Reused by both the bag drawer (one entry per cart line)
 * and any direct add-to-bag/checkout flow (UnlockClient, /comix/checkout — one
 * entry for the comic being unlocked, no cart line exists yet).
 *
 * Same action-sheet shell as ThemePickerSheet/ArtStylePickerSheet (the
 * project's one bottom-sheet convention) — framer-motion slide-up, #F1EDEF
 * panel, floating round close button, centered title. Selection itself is
 * shown only by the card's border + text color (no separate checkbox) — this
 * matches the final design handed off directly as a reference screenshot.
 */
export default function SizePickerModal({
  items,
  onCancel,
  onConfirm,
  isSubmitting,
}: {
  items: SizePickerItem[];
  onCancel: () => void;
  onConfirm: (selections: SizeSelection[]) => void;
  isSubmitting?: boolean;
}) {
  const [selections, setSelections] = useState<Record<string, BookPageCount>>(() =>
    Object.fromEntries(items.map((it) => [it.id, it.initialPageCount ?? 12] as const)),
  );

  const setPageCount = (lineId: string, pageCount: BookPageCount) =>
    setSelections((prev) => ({ ...prev, [lineId]: pageCount }));

  return (
    <AnimatePresence>
      {/* z-[300], not z-[60]: /comix/unlock also renders the PayPal button, whose SDK sets
          z-index:100 on its visible iframe and z-index:200 on a hidden "prerender" one it
          also keeps mounted — either would otherwise bleed through on top of this modal. */}
      <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center" onClick={onCancel}>
        <motion.div
          className="absolute inset-0 bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />

        <motion.div
          className="relative rounded-none sm:rounded-3xl w-full sm:max-w-[420px] h-[100dvh] sm:h-auto sm:max-h-[90dvh] overflow-y-auto shadow-2xl"
          style={{ background: "#F1EDEF" }}
          onClick={(e) => e.stopPropagation()}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", stiffness: 380, damping: 36 }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close"
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/10 transition-colors z-10 disabled:opacity-50"
          >
            <svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="px-4 pt-[52px] pb-4">
            <h3 className="text-[22px] font-semibold text-[#333333] text-center mb-9">
              Extra Pages, Extra Magic
            </h3>

            {items.map((item) => {
              const title = item.title || "Your comic book";
              const selected = selections[item.id] ?? 12;

              return (
                <div key={item.id} className="mb-3 last:mb-0">
                  {items.length > 1 && (
                    <p className="mb-2 truncate text-[13px] font-medium text-[#828282]">{title}</p>
                  )}
                  <div className="flex flex-col gap-2">
                    {([EXTENDED_INNER_PAGES, STANDARD_INNER_PAGES] as const).map((pageCount) => {
                      const isSelected = selected === pageCount;
                      // Customer-facing page count includes the front + back cover (matches
                      // the "14 pages" claim already used elsewhere in the unlock page's own
                      // FEATURES copy) — inner content pages + 2.
                      const displayPages = displayPageCount(pageCount);
                      return (
                        <button
                          key={pageCount}
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => setPageCount(item.id, pageCount)}
                          // 215px ≈ everything in the sheet that isn't a card (top pad, headline, gaps, button, bottom pad).
                          // Each card gets half of what's left, so a short phone crops the photo's white
                          // bottom edge a little instead of making the sheet scroll.
                          className="relative block w-full max-h-[calc((100dvh-215px)/2)] sm:max-h-[calc((90dvh-215px)/2)] overflow-hidden rounded-[20px] bg-white text-left shadow-[0_4px_4px_rgba(0,0,0,0.08)] disabled:opacity-50"
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={BOOK_PHOTO[pageCount]} alt="" className="block h-auto max-h-[inherit] w-full object-cover object-top" />
                          <p
                            className={`absolute inset-x-0 bottom-[14%] translate-y-[6px] text-center text-[16px] font-semibold ${
                              isSelected
                                ? "bg-gradient-to-r from-[#F52F81] to-[#FD7468] bg-clip-text text-transparent"
                                : "text-[#1D1D1F]"
                            }`}
                          >
                            {displayPages} pages
                            {pageCount === EXTENDED_INNER_PAGES && <span className="font-normal"> (+${item.extraPagesUpcharge})</span>}
                            {item.basePrice != null && (
                              <span className="block text-[14px] font-normal">
                                ${pageCount === EXTENDED_INNER_PAGES ? item.basePrice + item.extraPagesUpcharge : item.basePrice}/book
                              </span>
                            )}
                          </p>
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none absolute inset-0 rounded-[20px] ${
                              isSelected ? "" : "border border-black/10"
                            }`}
                            style={isSelected ? SELECTED_RING : undefined}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <PrimaryButton
              className="mt-2 block w-full"
              disabled={isSubmitting}
              onClick={() =>
                onConfirm(items.map((it) => ({ id: it.id, pageCount: selections[it.id] ?? 12 })))
              }
            >
              {isSubmitting ? "Updating…" : "Continue"}
            </PrimaryButton>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
